export type ErrorContext = "sign-in" | "code" | "book-code" | "upload" | "share" | "account";

function messageOf(error: unknown): string {
  if (typeof error === "string") return error;
  if (!error || typeof error !== "object") return "";
  const record = error as { message?: unknown; error_description?: unknown; code?: unknown };
  return [record.message, record.error_description, record.code]
    .filter((value): value is string => typeof value === "string")
    .join(" ");
}

/** Convert backend/browser errors into short, actionable language for the journal's readers. */
export function friendlyErrorMessage(
  error: unknown,
  context: ErrorContext,
  fallback?: string,
): string {
  const raw = messageOf(error);
  const normalized = raw.toLowerCase();

  if (/email rate limit exceeded|over_email_send_rate_limit|rate limit exceeded|too many requests/.test(normalized)) {
    return "Too many sign-in emails were sent. Please wait a few minutes and try again.";
  }
  if (/load failed|failed to fetch|networkerror|network request failed|fetch failed|connection (?:was )?lost|offline/.test(normalized)) {
    return "Check your internet connection and try again.";
  }

  if (context === "code" && /invalid (?:otp|token|code)|otp_expired|expired otp|token has expired/.test(normalized)) {
    return "That code didn’t work. Check the six digits or request a new sign-in email.";
  }
  if (context === "book-code") {
    if (/already been claimed|already claimed/.test(normalized)) {
      return "That book code has already been claimed. Check the code or contact the person who gave it to you.";
    }
    if (/couldn.t find that book code|book code.*(?:not found|does not exist)|valid book code/.test(normalized)) {
      return "We couldn’t find that book code. Check the letters and numbers and try again.";
    }
  }
  if (context === "share" && /story is no longer shared|share link.*(?:not found|inactive|revoked)/.test(normalized)) {
    return "This story is no longer shared.";
  }

  const defaults: Record<ErrorContext, string> = {
    "sign-in": "We couldn’t send your sign-in email. Please try again.",
    code: "We couldn’t check that code. Please try again or request a new email.",
    "book-code": "We couldn’t check that book code. Please try again.",
    upload: "We couldn’t save your recording. Your audio is still here; please try again.",
    share: "We couldn’t update sharing right now. Please try again.",
    account: "We couldn’t complete that account action. Please try again.",
  };
  return fallback ?? defaults[context];
}
